// src/app/components/atoms/ChipsAtom.tsx

import React, {
  forwardRef,
  Ref,
} from 'react';
import {
  Chips,
  ChipsChangeEvent,
  ChipsAddEvent,
  ChipsRemoveEvent,
  ChipsProps
} from 'primereact/chips';
import { chipsAccessibility } from './chipsAccessibility';
import './ChipsAtom.scss';


export interface ChipsAtomProps extends Omit<ChipsProps, 'value' | 'onChange'> {
  /** Controlled array of chip values */
  value: string[];
  /** Fires whenever the array of chips changes */
  onChange: (value: string[]) => void;
  /** Fires when a chip is added. Return `false` to prevent add. */
  onAdd?: (event: ChipsAddEvent) => void | boolean;
  /** Fires when a chip is removed */
  onRemove?: (event: ChipsRemoveEvent) => void;
  /** Shortcut for ref to the underlying `<input>` */
  inputRef?: Ref<HTMLInputElement>;
}

/**
 * A wrapper around PrimeReact’s <Chips> component
 * that normalizes onChange to a plain string[].
 */
const ChipsAtom = forwardRef<HTMLInputElement, ChipsAtomProps>((props, ref) => {
  const {
    value,
    onChange,
    onAdd,
    onRemove,
    onFocus,
    onBlur,
    onKeyDown,
    inputRef,
    pt,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    ...rest
  } = props;

  const handleChange = (e: ChipsChangeEvent) => {
    onChange(e.value ?? []);
  };

  return (
    <div className="prime-chips-wrapper">
    <Chips
      // Pass all other props (placeholder, disabled, max, etc.)
      {...rest}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      pt={chipsAccessibility(pt, { label: ariaLabel, labelledBy: ariaLabelledby, disabled: rest.disabled })}
      className="prime-chips"
      value={value}
      onChange={handleChange}
      onAdd={onAdd}
      onRemove={onRemove}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      // Merge ref and inputRef:
      inputRef={ref as Ref<HTMLInputElement> || inputRef}
    />
    </div>
  );
});

ChipsAtom.displayName = 'ChipsAtom';

export default ChipsAtom;
